import { useState } from 'react';
import { Button, Dialog, Field, Icon, useErrorDialog, useToast } from '@dayon/design-system';
import { api, ApiRequestError } from '../../../lib/api.ts';

/** The floor and the ceiling the route enforces, restated so the counter agrees. */
const MINIMUM = 10;
const MAXIMUM = 2000;

/**
 * Write an internal note on one player.
 *
 * ONE player, never a selection. Every other write in this module takes the
 * whole roster selection because a control, a status and a tier are the same
 * decision applied to many accounts. A note is not: the same sentence pasted
 * onto twelve players is twelve records that all describe one of them, and the
 * evidence trail it was meant to build becomes noise. So this is offered from
 * the profile and from the quick look, and not from the bulk bar.
 *
 * The form state lives HERE rather than inside <Dialog>, which unmounts its
 * children when closed — a half-typed note must survive a mis-clicked Cancel.
 */
export function NoteDialog({
  open,
  onClose,
  playerId,
  subject,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  playerId: string;
  /** Whose account this is, for the title. */
  subject: string;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmed = body.trim();
  /* Shown once the operator has started, never before: an error on an untouched
     field is a form telling somebody off for not having typed yet. */
  const error =
    body.length > 0 && trimmed.length < MINIMUM
      ? `At least ${MINIMUM} characters — this cannot be edited afterwards, so it has to say something.`
      : undefined;
  const ready = trimmed.length >= MINIMUM && trimmed.length <= MAXIMUM;

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      await api.post(`/api/players/${encodeURIComponent(playerId)}/notes`, { body: trimmed });
      toast('Note saved — it cannot be edited or removed, and it is in the audit trail.');
      setBody('');
      onClose();
      onDone();
    } catch (error_: unknown) {
      showError({
        title: 'Could not save that note',
        message:
          error_ instanceof ApiRequestError
            ? error_.message
            : 'Nothing was written — the note and its audit row land together or not at all.',
        ...(error_ instanceof ApiRequestError ? { code: error_.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Add a note on ${subject}`}
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save the note'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            <strong>Append-only, and that is the point.</strong>{' '}
            {/* A trigger refuses UPDATE and DELETE on player_note from every session. */}
            once saved, a note cannot be edited or deleted by anything on the platform — not this
            screen, not a background job, not anyone working behind the screens — so it is
            evidence rather than a draft. Correcting it
            means writing a second note that says so. Your name and the moment are stamped on it,
            and the body itself goes into the hash-chained audit trail beside it.
          </div>
        </div>

        <Field
          label="Note"
          error={error}
          hint={`Staff-only. This is what the next operator reads before deciding whether to lift a control. ${trimmed.length} of ${MAXIMUM} characters.`}
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={5}
              maxLength={MAXIMUM}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Called in about the held payout. Confirmed the rail account is theirs; asked them to resubmit the bank statement by Friday."
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
